import type { CrossCheckClause } from '../types/crossCheck';
import { TrackChangesParser } from '../word/utils/TrackChangesParser';
import { log } from './logging';

/**
 * Returns the client request XML string if the clause has XML content; otherwise null.
 */
export function getClientRequestXml(clause: CrossCheckClause): string | null {
  const cr = clause.client_request?.trim() || '';
  if (
    cr &&
    (cr.startsWith('<w:') ||
      cr.startsWith('<pkg:') ||
      cr.startsWith('<?xml') ||
      cr.includes('<w:p') ||
      cr.includes('<p '))
  ) {
    return cr;
  }
  if (clause.track_changes?.paragraph_xml) {
    return clause.track_changes.paragraph_xml;
  }
  return null;
}

/**
 * Returns plain text from the clause's client request suitable for searching
 * in the Word document (e.g. for paragraph index sync or navigate-by-content).
 */
export function getClientRequestSearchText(clause: CrossCheckClause): string {
  const xml = getClientRequestXml(clause);
  let result = '';

  if (xml) {
    try {
      result = TrackChangesParser.extractText(xml).replace(/\s+/g, ' ').trim();
    } catch {
      // fallthrough
    }
  }
  if (!result && clause.module_type === 'module_b' && clause.client_request) {
    try {
      const parsed = JSON.parse(clause.client_request);
      result = (
        (parsed.edited_text ?? parsed.original_text ?? clause.client_request) as string
      ).replace(/\s+/g, ' ').trim();
    } catch {
      result = (clause.client_request || '').replace(/\s+/g, ' ').trim();
    }
  }
  if (!result) {
    result = (clause.client_request || '').replace(/\s+/g, ' ').trim();
  }

  if (!result || result.trim().length === 0) {
    log('crossCheckDocumentUtils: getClientRequestSearchText empty', {
      clauseId: clause.id,
      hasClientRequest: !!clause.client_request,
      clientRequestLength: clause.client_request?.length ?? 0,
      hasTrackChangesParagraphXml: !!(clause as any).track_changes?.paragraph_xml,
      module_type: clause.module_type,
      hadXml: !!xml,
    });
  }

  return result;
}
